import {
  resolveObjectivesTrackerDetailRoute,
  TOOLS,
} from "../../../../../shared/lib/routesResolver";
import useAxios from "../../../../../hooks/useAxios";
import {
  catchFunction,
  dateTransformer,
} from "../../../../../shared/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  OBJECTIVE_TRACKER_BULK_UPLOAD,
  OBJECTIVE_TRACKER_BY_ID,
  OBJECTIVES_TRACKER as API_OBJECTIVES_TRACKER,
  OBJECTIVES_TRACKER_CLIENT_SELECTABLES,
} from "../../../../../shared/lib/apiEndpointsResolver";
import useConfirmDialog from "../../../../../shared/components/confirm-dialog/useConfirmDialog";
import SearchInput from "../../../../../shared/components/input/SearchInput";
import OverviewComponent from "../../../../../shared/components/overview-panel/OverviewComponent";
import OverviewTabContent from "../../../../../shared/components/overview-panel/OverviewTabContent";
import ObjectivesTrackerOverviewTable from "./ObjectivesTrackerOverviewTable";
import { useTracking } from "react-tracking";
import useLoading from "../../../../../shared/components/loading/useLoading";
import CreateObjectiveTrackerDialog from "./create/CreateObjectiveTrackerDialog";
import EditObjectiveTrackerDialog from "./edit/EditObjectiveTrackerDialog";
import useToastWrapper from "../../../../../hooks/useToastWrapper";
import { useTranslation } from "react-i18next";
import BulkLoadObjectiveTrackerDialog from "./bulk-load/BulkLoadObjectiveTrackerDialog";
import useLocalStorage from "../../../../../hooks/useLocalStorage";

const ObjectivesTrackerOverview = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const trackersRef = useRef([]);
  const [trackers, setTrackers] = useState([]);
  const [enabledTrackers, setEnabledTrackers] = useState([]);
  const [disabledTrackers, setDisabledTrackers] = useState([]);
  const deleteTrackerDialog = useConfirmDialog();
  const [trackerForDeletion, setTrackerForDeletion] = useState();
  const [clients, setClients] = useState([]);
  const { trackEvent } = useTracking();
  const [externalOpen, setExternalOpen] = useState({ externalOpen: false });
  const [selectedTracker, setSelectedTracker] = useState();
  const toast = useToastWrapper();
  const { t } = useTranslation();

  const storage = useLocalStorage();

  const breadcrumbItems = [
    { title: "objectivesTracker.overview.breadcrumb.first", link: TOOLS },
    { title: "objectivesTracker.overview.breadcrumb.second" },
  ];

  const onObjectiveTrackerCreation = (formValues) => {
    axios
      .post(API_OBJECTIVES_TRACKER, {
        name: formValues.name,
        clientId: formValues.clientId,
        dateRange: {
          from: formValues.beginDate,
          to: formValues.endDate,
        },
        goal: formValues.goal,
        createdBy: formValues.createdBy,
      })
      .then((response) =>
        navigate(resolveObjectivesTrackerDetailRoute(response.data.id), {
          state: { id: response.data.id },
        }),
      )
      .catch(catchFunction);
  };

  const filterTrackerByNameClientOrCreatedByFn = (tracker, searchTerm) => {
    return (
      tracker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tracker.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tracker.createdBy.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterTrackerByNameClientOrCreatedByAndStatusFn = (
    tracker,
    searchTerm,
    status,
  ) => {
    return (
      (tracker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tracker.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tracker.createdBy.toLowerCase().includes(searchTerm.toLowerCase())) &&
      tracker.enabled === status
    );
  };

  const filterTrackersByNameClientOrCreatedBy = (changeEvent) => {
    const searchTerm = changeEvent.target.value;
    setTrackers(
      trackersRef.current.filter((tracker) =>
        filterTrackerByNameClientOrCreatedByFn(tracker, searchTerm),
      ),
    );
    setEnabledTrackers(
      trackersRef.current.filter((tracker) =>
        filterTrackerByNameClientOrCreatedByAndStatusFn(
          tracker,
          searchTerm,
          true,
        ),
      ),
    );
    setDisabledTrackers(
      trackersRef.current.filter((tracker) =>
        filterTrackerByNameClientOrCreatedByAndStatusFn(
          tracker,
          searchTerm,
          false,
        ),
      ),
    );
  };

  const findClientSelectables = () => {
    axios
      .get(OBJECTIVES_TRACKER_CLIENT_SELECTABLES)
      .then((response) => setClients(response.data));
  };

  const findTrackers = () => {
    setLoading(true);
    axios
      .get(API_OBJECTIVES_TRACKER)
      .then((response) => {
        trackersRef.current = response.data;
        setTrackers(response.data);
        setEnabledTrackers(response.data.filter((x) => x.enabled === true));
        setDisabledTrackers(response.data.filter((x) => x.enabled === false));
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const deleteTracker = () => {
    setLoading(true);
    axios
      .delete(OBJECTIVE_TRACKER_BY_ID(trackerForDeletion.id))
      .then((_) => findTrackers())
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onDeleteTrackerClick = (id) => {
    const tracker = trackersRef.current.filter(
      (tracker) => tracker.id === id,
    )[0];
    setTrackerForDeletion(tracker);
    deleteTrackerDialog.display(`tracker ${tracker.name}`);
  };

  const onEditTrackerClick = (tracker) => {
    setExternalOpen({ ...externalOpen, externalOpen: true });
    setSelectedTracker(tracker);
  };

  const editTracker = (formValues) => {
    setLoading(true);
    const request = {
      name: formValues.name,
      goal: formValues.goal,
      dateRange: {
        from: formValues.beginDate,
        to: formValues.endDate,
      },
    };
    axios
      .put(OBJECTIVE_TRACKER_BY_ID(selectedTracker.id), request)
      .then((response) => {
        toast.success(
          t("objectivesTracker.editForm.success", { name: response.name }),
        );
        findTrackers();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onObjectiveTrackerBulkLoad = (formValues) => {
    setLoading(true);
    const formData = new FormData();
    /*
    const files = formValues.file;

    for (let i = 0; i < files.length; i++) {
      formData.append("file", files[i]);
    }
     */

    formData.append("file", formValues.file[0]);
    formData.append("name", formValues.name);
    formData.append("beginDate", dateTransformer(formValues.beginDate));
    formData.append("endDate", dateTransformer(formValues.endDate));
    formData.append("clientId", formValues.clientId);
    formData.append("goal", formValues.goal);
    formData.append("createdBy", formValues.createdBy);

    fetch(`${process.env.REACT_APP_BASE_URL}${OBJECTIVE_TRACKER_BULK_UPLOAD}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${storage.getAccessToken()}`,
      },
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("File upload failed");
        }
      })
      .then((data) => {
        toast.success(`File upload successfully`);
        navigate(resolveObjectivesTrackerDetailRoute(data.id), {
          state: { id: data.id },
        });
      })
      .catch((error) => {
        toast.error(`Error uploading the file: ${error}`);
        console.error("Error uploading file:", error);
      })
      .finally(() => setLoading(false));
  };

  const OverviewOptions = (
    <>
      <SearchInput
        onChangeHandler={filterTrackersByNameClientOrCreatedBy}
        placeholder="objectivesTracker.overview.searchInputPlaceholder"
      />
      <CreateObjectiveTrackerDialog
        onSubmitHandler={onObjectiveTrackerCreation}
        clients={clients}
      />
      <BulkLoadObjectiveTrackerDialog
        onSubmitHandler={onObjectiveTrackerBulkLoad}
        clients={clients}
      />
    </>
  );

  const objectivesTrackerTabs = [
    {
      trigger: {
        value: "all",
        label: "objectivesTracker.overview.tabs.trigger.all",
      },
      content: (
        <OverviewTabContent
          value="all"
          title="objectivesTracker.overview.card.all.title"
          description="objectivesTracker.overview.card.all.description"
        >
          <ObjectivesTrackerOverviewTable
            data={trackers}
            onEdit={onEditTrackerClick}
            onDelete={onDeleteTrackerClick}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "enabled",
        label: "objectivesTracker.overview.tabs.trigger.enabled",
      },
      content: (
        <OverviewTabContent
          value="enabled"
          title="objectivesTracker.overview.card.enabled.title"
          description="objectivesTracker.overview.card.enabled.description"
        >
          <ObjectivesTrackerOverviewTable
            data={enabledTrackers}
            onEdit={onEditTrackerClick}
            onDelete={onDeleteTrackerClick}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "disabled",
        label: "objectivesTracker.overview.tabs.trigger.disabled",
      },
      content: (
        <OverviewTabContent
          value="disabled"
          title="objectivesTracker.overview.card.disabled.title"
          description="objectivesTracker.overview.card.disabled.description"
        >
          <ObjectivesTrackerOverviewTable
            data={disabledTrackers}
            onEdit={onEditTrackerClick}
            onDelete={onDeleteTrackerClick}
          />
        </OverviewTabContent>
      ),
    },
  ];

  useEffect(() => {
    findTrackers();
    findClientSelectables();
    trackEvent({
      page: "objectives-tracker-overview",
      event: "page-load",
    });
  }, []);

  return (
    <>
      {deleteTrackerDialog.renderSafeDialog(deleteTracker)}
      <EditObjectiveTrackerDialog
        onSubmitHandler={editTracker}
        tracker={selectedTracker}
        externalOpen={externalOpen}
        hiddenClassName="hidden"
      />
      <OverviewComponent
        breadcrumbItems={breadcrumbItems}
        options={OverviewOptions}
        tabs={objectivesTrackerTabs}
        defaultTabValue="enabled"
      />
    </>
  );
};

export default ObjectivesTrackerOverview;
